// MODULES
import React from "react";
import PropTypes from "prop-types";

// CLASSES
import Session from "@classes/Session";
import Validator from "@classes/Validator";

// COMPONENTS
import DefaultFields from "../../../components/DefaultFields/DefaultFields";
import EditableField from "../../../components/EditableField/EditableField";
import FieldsCollection from "@components/FieldsCollection/FieldsCollection";

// HELPERS
import {createClassName} from "@helpers/utils";
import * as CobHelpers from "../../../Cob.helpers";
import fulfillmentCheck from "./CustomerInformation.fulfillment";
import CheckboxWithName from "../../../components/CheckboxWithName/CheckboxWithName";


export default class CobCustomerInformation extends React.PureComponent {
	static propTypes = {
		id: PropTypes.string.isRequired,
		title: PropTypes.string.isRequired,
		session: PropTypes.instanceOf(Session).isRequired,
		sectionNumber: PropTypes.number.isRequired,
		number: PropTypes.number.isRequired,
		prevState: PropTypes.object,
		onChange: PropTypes.func.isRequired,
	};

	static companyExclusive = false;
	static privateExclusive = false;
	static fields = _getFields();

	state = _getInitialState.call(this);

	componentDidUpdate(prevProps, prevState) {
		const {props, state} = this;
		const {id, onChange} = props;

		if (prevState.fields !== state.fields) {
			onChange(id, {...state}, fulfillmentCheck(props, state));
		}
	}

	render() {
		const {props, state} = this;
		const {title, session} = props;
		const classes = createClassName("Cob-CustomerInformation", {
			"Cob-View": true,
			"fulfilled": state.isFulfilled,
		});

		let ssnShort = state.fields.customer_social_security_number.replaceAll("-", "");
		if (ssnShort.length > 10) {
			ssnShort = ssnShort.substring(2, ssnShort.length);
		}

		let addressInSingleLine = state.fields.customer_address_street.length !== 0 ? state.fields.customer_address_street : "-";
		addressInSingleLine += " ";
		addressInSingleLine += state.fields.customer_address_zipcode.length !== 0 ? state.fields.customer_address_zipcode : "-";
		addressInSingleLine += " ";
		addressInSingleLine += state.fields.customer_address_city.length !== 0 ? state.fields.customer_address_city : "-";

		return (
			<div className={classes}>
				<DefaultFields fields={this.constructor.fields}/>

				<div className="Cob-Fields">
					<h2>{title}</h2>

					<FieldsCollection
						fieldsPerRow={session.isCompany ? 2 : 3}
						fields={[
							{
								render: (
									<EditableField
										name="customer_social_security_number"
										label={session.isCompany ? "Organisationsnummer" : "Personnummer"}
										placeholder={session.isCompany ? "XXXXXX-YYYY" : "ÅÅÅÅÅMMDD-XXXX"}
										value={state.fields.customer_social_security_number}
										readOnly={true}
										valid={true}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
										required
									/>
								),
							},
							{
								render: (
									<EditableField
										name="customer_firstname"
										label={session.isCompany ? "Bolag" : "Förnamn"}
										value={state.fields.customer_firstname}
										valid={!!session.lookupData || state.validFields.customer_firstname}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
										required
									/>
								),
							},
							{
								render: (
									<EditableField
										name="customer_lastname"
										label="Efternamn"
										value={state.fields.customer_lastname}
										valid={!!session.lookupData || state.validFields.customer_lastname}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
										required
									/>
								),
								display: !session.isCompany,
							}
						]}
					/>

					<CheckboxWithName
						style={{margin: "20px 0 10px 0"}}
						label="Ändring av telefonnummer"
						name="checkbox_change_phone"
						value={state.fields.checkbox_change_phone.checked ? "yes" : "no"}
						checked={state.fields.checkbox_change_phone.checked}
						onChange={this._onCheckboxChange}
					/>

					{state.fields.checkbox_change_phone.checked && (
						<React.Fragment>
							<FieldsCollection
								fieldsPerRow={3}
								fields={[
									{
										render: (
											<EditableField
												type="tel"
												name="customer_phone_old"
												label="Gammalt telefonnr"
												value={state.fields.customer_phone_old}
												valid={state.validFields.customer_phone_old}
												onChange={this._onFieldChange}
												onBlur={this._onFieldBlur}
												required
											/>
										),
									},
									{
										render: (
											<EditableField
												type="phone"
												name="customer_phone_new"
												label="Nytt telefonnr"
												value={state.fields.customer_phone_new}
												valid={state.validFields.customer_phone_new}
												onChange={this._onFieldChange}
												onBlur={this._onFieldBlur}
												required
											/>
										),
									}
								]}
							/>
						</React.Fragment>
					)}

					<CheckboxWithName
						style={{margin: "20px 0 10px 0"}}
						label="Ändring av föranmält bankkontonummer"
						name="checkbox_change_account"
						value={state.fields.checkbox_change_account.checked ? "yes" : "no"}
						checked={state.fields.checkbox_change_account.checked}
						onChange={this._onCheckboxChange}
					/>

					{state.fields.checkbox_change_account.checked && (
						<React.Fragment>
							<FieldsCollection
								fieldsPerRow={3}
								fields={[
									{
										render: (
											<EditableField
												type="tel"
												name="customer_autogiro_bank_clearingno_old"
												label="Gammalt clearingnummer"
												value={state.fields.customer_autogiro_bank_clearingno_old}
												valid={state.validFields.customer_autogiro_bank_clearingno_old}
												onChange={this._onFieldChange}
												onBlur={this._onFieldBlur}
												required
											/>
										),
									},
									{
										render: (
											<EditableField
												type="tel"
												name="customer_autogiro_bankno_old"
												label="Gammalt kontonummer"
												value={state.fields.customer_autogiro_bankno_old}
												valid={state.validFields.customer_autogiro_bankno_old}
												onChange={this._onFieldChange}
												onBlur={this._onFieldBlur}
												required
											/>
										),
									},
								]}
							/>

							<FieldsCollection
								fieldsPerRow={3}
								fields={[
									{
										render: (
											<EditableField
												type="phone"
												name="customer_autogiro_bank_clearingno"
												label="Nytt clearingnummer"
												value={state.fields.customer_autogiro_bank_clearingno}
												valid={state.validFields.customer_autogiro_bank_clearingno}
												onChange={this._onFieldChange}
												onBlur={this._onFieldBlur}
												required
											/>
										),
									},
									{
										render: (
											<EditableField
												type="phone"
												name="customer_autogiro_bankno"
												label="Nytt kontonummer"
												value={state.fields.customer_autogiro_bankno}
												valid={state.validFields.customer_autogiro_bankno}
												onChange={this._onFieldChange}
												onBlur={this._onFieldBlur}
												required
											/>
										),
									}
								]}
							/>
						</React.Fragment>
					)}

					<CheckboxWithName
						style={{margin: "20px 0 10px 0"}}
						label="Ändring av e-postadress"
						name="checkbox_change_email"
						value={state.fields.checkbox_change_email.checked ? "yes" : "no"}
						checked={state.fields.checkbox_change_email.checked}
						onChange={this._onCheckboxChange}
					/>

					{state.fields.checkbox_change_email.checked && (
						<React.Fragment>
							<FieldsCollection
								fieldsPerRow={2}
								fields={[
									{
										render: (
											<EditableField
												type="email"
												name="customer_email_old"
												label="Gammal e-postadress"
												value={state.fields.customer_email_old}
												valid={state.validFields.customer_email_old}
												onChange={this._onFieldChange}
												onBlur={this._onFieldBlur}
												required
											/>
										),
									},
									{
										render: (
											<EditableField
												type="email"
												name="customer_email_new"
												label="Ny e-postadress"
												value={state.fields.customer_email_new}
												valid={state.validFields.customer_email_new}
												onChange={this._onFieldChange}
												onBlur={this._onFieldBlur}
												required
											/>
										),
									}
								]}
							/>

							{!session.isCompany && (
								<React.Fragment>
									<p style={{margin: "20px 0"}}>
										Det digitala avtalet kommer att skickas ut för signering till e-postadressen du anger
										som 'Ny e-postadress'.
									</p>

									<input type="hidden"
										   name="customer_email"
										   value={state.fields.customer_email_new}/>
								</React.Fragment>
							)}
						</React.Fragment>
					)}

					{!session.isCompany && !state.fields.checkbox_change_email.checked && (
						<React.Fragment>
							<p style={{margin: "20px 0 10px 0"}}>
								Det digitala avtalet kommer att skickas ut för signering till e-postadressen du anger
								nedan.
							</p>

							<FieldsCollection
								fieldsPerRow={2}
								fields={[
									{
										render: (
											<EditableField
												type="email"
												name="customer_email"
												label="E-postadress"
												value={state.fields.customer_email}
												valid={state.validFields.customer_email}
												onChange={this._onFieldChange}
												onBlur={this._onFieldBlur}
												required
											/>
										),
									}
								]}
							/>
						</React.Fragment>
					)}

					<input type="hidden"
						   name="customer_social_security_number"
						   value={ssnShort}/>

					<input type="hidden"
						   name="customer_address"
						   value={addressInSingleLine}/>

				</div>
			</div>
		);
	}

	// Internal methods
	_fieldValidator = (name, value) => {
		if (name === "customer_social_security_number") {
			return this.props.session.isCompany ? Validator.validateOrgNum(value, true) : Validator.validateSsn(value, true);
		}
		else if (name === "customer_email") {
			return Validator.validateEmail(value);
		}
		else {
			return value.length > 0;
		}
	};

	_onFieldChange = (name, value) => {
		CobHelpers.onViewFieldChange.call(this, name, value);
	};

	_onFieldBlur = (name) => {
		CobHelpers.onViewFieldBlur.call(this, name);
	};

	_onCheckboxChange = (name, checked) => {
		this.setState({
			fields: {
				...this.state.fields,
				[name]: {...this.state.fields[name], checked}
			}
		});
	};
}


// PRIVATE FUNCTIONS
function _getFields() {
	return [
		"customer_firstname",
		"customer_lastname",
		"customer_social_security_number",
		"customer_email"
	];
}

function _getInitialState() {
	if (this.props.prevState) return {...this.props.prevState};

	const {session} = this.props;
	const {isCompany} = session;
	const getLookupData = (name) => session.lookupData ? session.lookupData[name] || "" : "";

	return {
		validFields: !session.lookupData ? {} : {
			"is_company": true,
			"customer_firstname": !!getLookupData(isCompany ? "companyName" : "firstName"),
			"customer_social_security_number": !!getLookupData(isCompany ? "orgNumber" : "ssn"),
			...(
				!isCompany ? {
					"customer_lastname": !!getLookupData("lastName")
				} : undefined
			),
		},
		fields: {
			"is_company": isCompany,
			"customer_firstname": getLookupData(isCompany ? "companyName" : "firstName"),
			"customer_lastname": !isCompany ? getLookupData("lastName") : "",
			"customer_social_security_number": getLookupData(isCompany ? "orgNumber" : "ssn"),
			"customer_address_street": getLookupData("street"),
			"customer_address_zipcode": getLookupData("zipCode"),
			"customer_address_city": getLookupData("city"),
			"customer_email": "",

			"customer_email_old": "",
			"customer_email_new": "",

			"customer_phone_old": "",
			"customer_phone_new": "",

			"customer_autogiro_bank_clearingno_old": "",
			"customer_autogiro_bank_clearingno": "",

			"customer_autogiro_bankno_old": "",
			"customer_autogiro_bankno": "",

			"checkbox_change_phone": {
				checked: false
			},

			"checkbox_change_account": {
				checked: false
			},

			"checkbox_change_email": {
				checked: false
			},

			"checkbox_same_email": {
				checked: false
			}
		},
	};
}


// WEBPACK FOOTER //
// ./src/routes/Cob/views/CustomerInformation/fair-kundinfo/CustomerInformation.js